import React, { useRef, useState, useEffect, Suspense, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import {
    Float,
    PresentationControls,
    Points,
    PointMaterial
} from '@react-three/drei';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import * as THREE from 'three';
import * as random from 'maath/random/dist/maath-random.esm';
import WebGLErrorBoundary from './WebGLErrorBoundary';

// Interactive Star Field for Hero - Matches Background3D.jsx style
const ShootingStar = () => {
    const ref = useRef();
    const [active, setActive] = useState(false);

    useEffect(() => {
        const interval = setInterval(() => {
            setActive(true);
            setTimeout(() => setActive(false), 1500); // Shorter duration
        }, 4000); // More frequent (every 4 seconds)
        return () => clearInterval(interval);
    }, []);

    useFrame((state, delta) => {
        if (active && ref.current) {
            // Faster movement for more dramatic effect
            ref.current.position.x += delta * 25;
            ref.current.position.y -= delta * 25;
            ref.current.position.z -= delta * 8;

            if (ref.current.position.x > 20) {
                ref.current.position.set(-20, 20, -5);
            }
        }
    });

    if (!active) return null;

    return (
        <mesh ref={ref} position={[-20, 20, -5]}>
            <sphereGeometry args={[0.15, 16, 16]} /> {/* Larger shooting star */}
            <meshStandardMaterial color="#ffffff" emissive="#ffffff" emissiveIntensity={5} />
        </mesh>
    );
};

const HeroStars = () => {
    const ref = useRef();

    // DENSE STAR FIELD: 5000 stars for real sky look
    // Range: x/y ±100, z ±50
    const positions = useMemo(() => {
        const pos = new Float32Array(5000 * 3);
        for (let i = 0; i < 5000; i++) {
            pos[i * 3] = (Math.random() - 0.5) * 200;
            pos[i * 3 + 1] = (Math.random() - 0.5) * 200;
            pos[i * 3 + 2] = (Math.random() - 0.5) * 100;
        }
        return pos;
    }, []);

    useFrame((state, delta) => {
        if (ref.current) {
            const positions = ref.current.geometry.attributes.position.array;
            const speed = delta * 4; // Moderate speed

            for (let i = 0; i < 5000; i++) {
                positions[i * 3] += speed;
                positions[i * 3 + 1] -= speed;

                if (positions[i * 3] > 100) {
                    positions[i * 3] = -100;
                    positions[i * 3 + 1] = (Math.random() - 0.5) * 200;
                }
                if (positions[i * 3 + 1] < -100) {
                    positions[i * 3 + 1] = 100;
                    positions[i * 3] = (Math.random() - 0.5) * 200;
                }
            }
            ref.current.geometry.attributes.position.needsUpdate = true;

            const { x, y } = state.mouse;
            ref.current.position.x = THREE.MathUtils.lerp(ref.current.position.x, x * 2, 0.1);
            ref.current.position.y = THREE.MathUtils.lerp(ref.current.position.y, y * 2, 0.1);
        }
    });

    return (
        <group rotation={[0, 0, Math.PI / 8]}>
            <points ref={ref}>
                <bufferGeometry>
                    <bufferAttribute
                        attach="attributes-position"
                        count={5000}
                        array={positions}
                        itemSize={3}
                    />
                </bufferGeometry>
                <PointMaterial
                    transparent
                    color="#ffffff"
                    size={0.15} // Dense but visible
                    sizeAttenuation={true}
                    depthWrite={false}
                    opacity={0.8} // Reduce opacity to prevent washout
                    blending={THREE.AdditiveBlending}
                />
            </points>
            <ShootingStar />
        </group>
    );
};

/* =====================================================
   HERO 3D SECTION - GALAXY EDITION
   ===================================================== */
const Hero3D = () => {
    const [is3DLoaded, setIs3DLoaded] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [displayText, setDisplayText] = useState('');
    const fullText = "GALACTIC";

    const { scrollYProgress } = useScroll();
    const y1 = useTransform(scrollYProgress, [0, 1], [0, -200]);

    useEffect(() => {
        let currentIndex = 0;
        const interval = setInterval(() => {
            if (currentIndex <= fullText.length) {
                setDisplayText(fullText.slice(0, currentIndex));
                currentIndex++;
            } else {
                clearInterval(interval);
            }
        }, 120);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        setIs3DLoaded(true);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <div className="relative w-full h-screen overflow-hidden flex items-center justify-center transition-opacity duration-1000 ease-in-out bg-gradient-to-b from-[#000000] via-[#0a0a2e] to-[#16213e]">

            {/* Cosmic gradient overlay for depth */}
            <div className="absolute inset-0 bg-gradient-radial from-transparent via-[#1a1a3e]/30 to-black/50 pointer-events-none" />

            {/* 3D Scene - Enhanced Stars */}
            <div className={`absolute inset-0 z-0 transition-opacity duration-1000 ${is3DLoaded ? 'opacity-100' : 'opacity-0'}`}>
                <WebGLErrorBoundary>
                    <Canvas
                        camera={{ position: [0, 0, 15], fov: 60 }}
                        dpr={[1, 1.5]}
                        gl={{
                            antialias: false,
                            powerPreference: "high-performance",
                            alpha: true, // Enable transparency for gradient background
                        }}
                    >
                        {/* NO <color> tag needed — alpha:true makes canvas transparent */}

                        {/* Deep space lighting for star glow */}
                        <ambientLight intensity={0.5} color="#ffffff" />
                        <pointLight position={[10, 10, 10]} intensity={1} color="#ffffff" />
                        <pointLight position={[-10, -10, -10]} intensity={1} color="#1b263b" />

                        <PresentationControls
                            global
                            config={{ mass: 1, tension: 500 }}
                            snap={{ mass: 2, tension: 1500 }}
                            rotation={[0, 0, 0]}
                            polar={[-Math.PI / 6, Math.PI / 6]}
                            azimuth={[-Math.PI / 4, Math.PI / 4]}
                        >
                            <HeroStars />
                        </PresentationControls>
                    </Canvas>
                </WebGLErrorBoundary>
            </div>

            {/* Overlay Content */}
            <motion.div
                style={{ y: y1 }}
                className="relative z-10 w-full max-w-7xl mx-auto px-6 text-center"
            >
                <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 1.5, ease: "easeOut" }}
                >
                    <h1 className="text-6xl sm:text-8xl md:text-[12rem] font-display font-black text-white mb-4 tracking-tighter leading-none drop-shadow-[0_0_50px_rgba(255,255,255,0.4)]">
                        TRENDZ<br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 via-white to-red-600 animate-gradient-x">
                            {displayText}
                        </span>
                    </h1>
                    <p className="text-lg md:text-2xl text-gray-400 mb-12 max-w-2xl mx-auto font-light tracking-[0.3em] uppercase">
                        Beyond Borders / Infinite Style
                    </p>
                    <div className="flex flex-wrap justify-center gap-8">
                        <Link to="/products" className="group relative px-12 py-5 bg-white text-black font-black rounded-full overflow-hidden transition-all hover:scale-110 active:scale-95 shadow-[0_0_50px_rgba(255,255,255,0.3)]">
                            <span className="relative z-10 font-bold">DISCOVER SHOP</span>
                            <div className="absolute inset-0 bg-red-600 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
                        </Link>
                        <Link to="/about" className="px-12 py-5 border-2 border-white/20 text-white font-bold rounded-full hover:bg-white/10 transition-all backdrop-blur-md">
                            OUR STORY
                        </Link>
                    </div>
                </motion.div>
            </motion.div>

            {/* Depth & Branding Overlays */}
            <div className="absolute bottom-10 left-10 z-20 hidden md:block">
                <div className="flex flex-col gap-2">
                    <div className="h-0.5 w-24 bg-red-600 animate-pulse" />
                    <span className="text-xs text-white/60 font-mono tracking-widest uppercase">GALAXY_OS v2.0 - ONLINE</span>
                </div>
            </div>

            <div className="absolute bottom-0 left-0 right-0 h-64 bg-gradient-to-t from-black to-transparent pointer-events-none" />

            {/* Touch Me Popup */}
            <div className="absolute bottom-8 right-8 z-20 hidden md:flex items-center gap-2 animate-bounce">
                <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-4 py-2 shadow-lg">
                    <span className="text-xs text-white/80 font-mono tracking-widest">✨ TOUCH THE STARS</span>
                </div>
            </div>
        </div>
    );
};

export default Hero3D;

